import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Alert
} from '@mui/material';

const MovimientoCajaDialog = ({
    open,
    onClose,
    onSave,
    caja,
    conceptos = [],
    tipoOperacionDefault = 'EGRESO' // 'EGRESO' o 'INGRESO'
}) => {
    const [idTipoMovimiento, setIdTipoMovimiento] = useState(tipoOperacionDefault === 'INGRESO' ? 354 : 352);
    const [idConceptoGasto, setIdConceptoGasto] = useState(conceptos[0]?.id || 381);
    const [tipoComprobante, setTipoComprobante] = useState('BOLETA');
    const [serieNumero, setSerieNumero] = useState('');
    const [docProveedor, setDocProveedor] = useState('');
    const [razonProveedor, setRazonProveedor] = useState('');
    const [conceptoGastoTexto, setConceptoGastoTexto] = useState('');
    const [monto, setMonto] = useState('');
    const [nombreRendidor, setNombreRendidor] = useState('');
    const [observaciones, setObservaciones] = useState('');

    useEffect(() => {
        setIdTipoMovimiento(tipoOperacionDefault === 'INGRESO' ? 354 : 352);
        if (conceptos.length > 0 && !idConceptoGasto) {
            setIdConceptoGasto(conceptos[0].id);
        }
    }, [tipoOperacionDefault, conceptos, open]);

    if (!caja) return null;

    const esEgreso = idTipoMovimiento === 352 || idTipoMovimiento === 353;

    const handleSubmit = (e) => {
        e.preventDefault();
        const numMonto = Number(monto);
        if (numMonto <= 0) {
            alert('El monto debe ser mayor a 0.');
            return;
        }

        if (esEgreso && numMonto > caja.saldoDisponible) {
            alert(`Saldo insuficiente en caja. Disponible: S/ ${caja.saldoDisponible.toFixed(2)}`);
            return;
        }

        const conceptoObj = conceptos.find((c) => c.id === idConceptoGasto);
        const conceptoDesc = conceptoGastoTexto.trim() || conceptoObj?.descripcion || 'Gasto de caja chica';

        const model = {
            idCajaChica: caja.id,
            idTipoMovimiento: Number(idTipoMovimiento),
            tipoComprobante,
            serieNumeroComprobante: serieNumero.trim(),
            docIdentidadProveedor: docProveedor.trim(),
            razonSocialProveedor: razonProveedor.trim(),
            idConceptoGasto: Number(idConceptoGasto),
            conceptoGasto: conceptoDesc,
            montoEgreso: esEgreso ? numMonto : 0,
            montoIngreso: !esEgreso ? numMonto : 0,
            nombreRendidor: nombreRendidor.trim(),
            observaciones: observaciones.trim()
        };

        onSave(model);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 'bold', color: esEgreso ? '#b91c1c' : '#15803d' }}>
                {esEgreso ? 'Registrar Egreso / Gasto de Caja Chica' : 'Registrar Reembolso / Ingreso a Caja'}
            </DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent dividers>
                    <Alert severity={esEgreso ? 'error' : 'success'} sx={{ mb: 2 }}>
                        Caja: <b>{caja.codigo} - {caja.nombre}</b> | Saldo Disponible Actual: <b>S/ {Number(caja.saldoDisponible).toFixed(2)}</b>
                    </Alert>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small" required>
                                <InputLabel>Tipo de Operación</InputLabel>
                                <Select
                                    value={idTipoMovimiento}
                                    onChange={(e) => setIdTipoMovimiento(e.target.value)}
                                    label="Tipo de Operación"
                                >
                                    <MenuItem value={352}>Egreso por Gasto Menor</MenuItem>
                                    <MenuItem value={353}>Vale Provisional por Rendir</MenuItem>
                                    <MenuItem value={354}>Reembolso / Reposición</MenuItem>
                                    <MenuItem value={355}>Devolución de Vale</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small" required>
                                <InputLabel>Concepto de Gasto</InputLabel>
                                <Select
                                    value={idConceptoGasto}
                                    onChange={(e) => setIdConceptoGasto(e.target.value)}
                                    label="Concepto de Gasto"
                                >
                                    {conceptos.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c.descripcion}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Monto de la Operación (S/)"
                                type="number"
                                inputProps={{ min: 0.1, step: '0.10' }}
                                value={monto}
                                onChange={(e) => setMonto(e.target.value)}
                                required
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Tipo de Comprobante</InputLabel>
                                <Select
                                    value={tipoComprobante}
                                    onChange={(e) => setTipoComprobante(e.target.value)}
                                    label="Tipo de Comprobante"
                                >
                                    <MenuItem value="BOLETA">Boleta de Venta</MenuItem>
                                    <MenuItem value="FACTURA">Factura Comercial</MenuItem>
                                    <MenuItem value="RECIBO_HONORARIOS">Recibo por Honorarios</MenuItem>
                                    <MenuItem value="VALE_INTERNO">Vale Provisional / Interno</MenuItem>
                                    <MenuItem value="TICKET">Ticket / Comprobante POS</MenuItem>
                                    <MenuItem value="DECLARACION_JURADA">Declaración Jurada de Gasto</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Serie y Número de Comprobante"
                                value={serieNumero}
                                onChange={(e) => setSerieNumero(e.target.value)}
                                placeholder="Ej: B001-002498"
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Personal Solicitante / Rendidor"
                                value={nombreRendidor}
                                onChange={(e) => setNombreRendidor(e.target.value)}
                                placeholder="Nombre de quien recibe el dinero"
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="RUC / DNI Proveedor"
                                value={docProveedor}
                                onChange={(e) => setDocProveedor(e.target.value)}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Razón Social / Proveedor"
                                value={razonProveedor}
                                onChange={(e) => setRazonProveedor(e.target.value)}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label="Detalle Específico del Gasto"
                                value={conceptoGastoTexto}
                                onChange={(e) => setConceptoGastoTexto(e.target.value)}
                                placeholder="Ej: Pasajes en taxi para tramitar firmas en el banco..."
                                multiline
                                rows={2}
                                fullWidth
                                size="small"
                                required
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={onClose} color="inherit">
                        Cancelar
                    </Button>
                    <Button type="submit" variant="contained" color={esEgreso ? 'error' : 'success'}>
                        {esEgreso ? 'Registrar Egreso' : 'Registrar Ingreso'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default MovimientoCajaDialog;
